// Character Types for Helix 2000 Game Loop Prototype

// Level types live in LevelTypes.ts — re-exported here for backward compat
export type { GridLevel, LevelEntity, Platform, PlatformerLevel, Position, SpawnRule, Velocity } from './LevelTypes';

// Types extracted to domain files — imported locally for Character/Helix2000Character interfaces
import type { JobClass, TalentTree, Profession, ProfessionType } from './types/Progression';
import type { ActionBar, KeyBindingsConfig } from './types/Input';
import type { AnimationComponent } from './types/Combat';
import type { ProgressionCostOffersWire } from './types/ProgressionCost';
import type { PhysicsEntity, MovementStats } from './types/Physics';
export type { PhysicsEntity, Projectile, MovementStats } from './types/Physics';

export interface WorldContext {
  mapId: string;
  channelId: string;
  worldId: string;
  realmId: string;
}

export enum CharacterClass {
  WARRIOR = 'warrior',
  MAGE = 'mage',
  HUNTER = 'hunter',
  PALADIN = 'paladin',
  ROGUE = 'rogue',
  SHAMAN = 'shaman',
  PRIEST = 'priest',
  WARLOCK = 'warlock',
  DRUID = 'druid',
  DEV = 'dev'  // Developer class with special abilities for testing
}

export interface DebugCreationOptions {
  startLevel?: number;
  startMap?: string;
  startGear?: 'naked' | 'starter' | 'god';
  godMode?: boolean;
  infiniteResources?: boolean;
}

export interface Ability {
  id: string;
  name: string;
  damage: number;
  manaCost: number;
  cooldown: number;
  lastUsed: number;
  range: number;
  castTime: number;
  description: string;
  icon?: string;
}

export type ItemType = 'weapon' | 'armor' | 'consumable' | 'material' | 'currency';
export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'unique';

export interface InventoryItem {
  id: string;
  name: string;
  type: ItemType;
  quantity: number;
  maxStack: number;
  rarity?: ItemRarity;
  icon?: string;
  stats?: Partial<CharacterStats>;
  description: string;
}

export interface Inventory {
  items: InventoryItem[];
  maxSlots: number;
  gold: number;
}

// Sub-Inventory Systems (Phase 5)
export interface BankVault {
  items: InventoryItem[];
  maxSlots: number;
  gold: number;
}

export interface TradeOffer {
  playerId: string;
  targetId: string;
  items: InventoryItem[];
  gold: number;
  isLocked: boolean;
  isAccepted: boolean;
}

export interface CraftingIngredient {
  itemId: string;
  quantity: number;
}

export interface CraftingRecipe {
  id: string;
  name: string;
  profession: ProfessionType;
  requiredLevel: number;
  ingredients: CraftingIngredient[];
  outputItemId: string;
  outputQuantity: number;
  timeToCraftMs?: number;
}

// Quest Types
export type QuestType = 'kill' | 'collect' | 'explore' | 'level' | 'equip';

export interface Quest {
  id: string;
  name: string;
  description: string;
  type: QuestType;
  /** Generated quest authority's hand-in NPC id/name, when client hydration has run. */
  endNpcId?: string;
  endNpcName?: string;
  targetId: string; // e.g., 'pig'
  targetCount: number;
  currentCount: number;
  completed: boolean;
  /**
   * Per-objective progress (multi-objective quests). When present with length>1
   * the HUD renders one row per objective. targetCount/currentCount mirror
   * objectives[0] for back-compat with single-objective consumers.
   */
  objectives?: {
    type?: 'kill' | 'collect' | 'explore' | 'talk' | 'level' | 'equip';
    targetId?: string;
    targetName?: string;
    description?: string;
    targetCount: number;
    currentCount: number;
  }[];
  rewards: {
    xp: number;
    gold: number;
    items?: InventoryItem[];
  };
}

export interface QuestLog {
  active: Quest[];
  completed: string[]; // IDs of completed quests
}

// Appearance types: canonical definitions live in shared/types/AppearanceTypes.ts
export type {
  GhostAppearance,
  GhostProps,
  WeaponConfig,
  EquipmentConfig,
} from './types/AppearanceTypes';

// Local import for use within this file
import type { GhostAppearance } from './types/AppearanceTypes';
import type { ResolvedPlayerEquipmentVisuals } from './equipmentVisuals';

export interface Character {
  _id: string; // Database ID
  id: string; // Unified ID access (same as _id)
  name: string;
  class: CharacterClass;
  job?: JobClass; // Current job/advanced class
  appearance?: GhostAppearance;
  level: number;
  xp: number;
  skillPoints: number; // Points available for talent trees
  statPoints: number; // Points available for primary stats (STR, DEX, etc.)
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
  abilities: Ability[];
  talents: TalentTree; // Talent tree allocations
  professions: Profession[]; // Gathering/crafting professions
  color: string;
  sprite?: string;
  stats: CharacterStats;
  inventory: Inventory;
  equipment?: Array<[string, any]>;
  quests: QuestLog;
  created: number;
  lastPlayed: number;
  isInvincible?: boolean;
  invincibilityTimer?: number;
  isOnline?: boolean;
  gmLevel?: number;

  // Base Physics (Required for engine reliability)
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  width: number;
  height: number;
  movementStats: MovementStats;

  // State Flags
  isGrounded: boolean;
  isJumping?: boolean;
  isMoving?: boolean;
  isClimbing?: boolean;
  /** Crouch pose (lane AZ) — see PhysicsEntity.isCrouching. */
  isCrouching?: boolean;
  facing?: 'left' | 'right';
  onPlatform?: boolean;
  platformId?: string;
  jumpCount: number;
  maxJumps: number;
  lastGroundedTime?: number;
  jumpBufferTime?: number;
  lastJumpTime?: number;

  // Unified Animation State
  animation?: AnimationComponent;
  animationState?: string;

  // Attack State
  isAttacking?: boolean;
  attackType?: 'basic' | 'secondary' | 'none';

  // UI Configuration (saveable per-character)
  actionBar?: ActionBar;
  /** Private server-authoritative learned set, mirrored for progression UI.
   * Casting remains curve-gated until the dedicated cutover slice. */
  learnedAbilities?: string[];
  /** Private server-authoritative pocket maps this character discovered (atlas known places). */
  knownPlaces?: string[];
  /** Private server-authoritative one-time world rewards this character claimed (`<mapId>:<rewardId>`). */
  claimedRewards?: string[];
  /** Transient owner-only server quotes; never persisted by CharacterService. */
  progressionCostOffers?: ProgressionCostOffersWire;
  keyBindings?: KeyBindingsConfig;
  cooldowns?: Record<string, number>;
  trainingProgress?: Record<string, number>; // Skill training progress

  // BIG-EQUIP-VIS-01 S5 — transient authoritative equipment projection.
  // NOT persisted by CharacterService (never saved to DB). Set by
  // PlayerManager from the nested `player.equipment` schema subtree via
  // `net:localEquipmentVisuals`. The renderer composes this with
  // `appearance` to produce the authoritative render appearance (S2).
  // Lives here, NOT on `appearance`, so CharacterService persistence
  // never sees it.
  equipmentVisuals?: ResolvedPlayerEquipmentVisuals;
}

export enum StatType {
  STRENGTH = 'strength',
  DEXTERITY = 'dexterity',
  INTELLIGENCE = 'intelligence',
  LUCK = 'luck',
  HEALTH = 'hp',
  MANA = 'mp',
  MAX_HEALTH = 'maxHp',
  MAX_MANA = 'maxMp',
  ATTACK = 'attack',
  MAGIC_ATTACK = 'magicAttack',
  DEFENSE = 'defense',
  MAGIC_DEFENSE = 'magicDefense',
  ACCURACY = 'accuracy',
  AVOIDABILITY = 'avoidability',
  SPEED = 'speed',
  JUMP = 'jump',
  CRITICAL_RATE = 'criticalRate',
  CRITICAL_DAMAGE = 'criticalDamage'
}

export interface CharacterStats {
  // Primary Stats
  [StatType.STRENGTH]: number;
  [StatType.DEXTERITY]: number;
  [StatType.INTELLIGENCE]: number;
  [StatType.LUCK]: number;

  // Resources
  [StatType.HEALTH]?: number;
  [StatType.MANA]?: number;
  [StatType.MAX_HEALTH]?: number;
  [StatType.MAX_MANA]?: number;

  // Secondary Stats
  [StatType.ATTACK]: number;
  [StatType.MAGIC_ATTACK]: number;
  [StatType.DEFENSE]: number;
  [StatType.MAGIC_DEFENSE]: number;
  [StatType.ACCURACY]: number;
  [StatType.AVOIDABILITY]: number;
  [StatType.SPEED]: number;
  [StatType.JUMP]: number;
  [StatType.CRITICAL_RATE]: number;
  [StatType.CRITICAL_DAMAGE]: number;
}

// Position and Velocity are now imported from LevelTypes.ts
// to avoid circular dependencies and type duplication.

export interface Helix2000Character extends Character {
  // Now inherits width, height, position, velocity from Character
  // Physics flags are also on Character base now.

  // Custom physics properties (often used for advanced movement)

  // Backward compatible stat access (use stats.magicAttack preferrably)
  magicAttack?: number;

  // Combat State
  lastGCDTime?: number;
  attackSpeed?: number; // Multiplier (1.0 = normal)
  weaponReach?: number; // Multiplier (1.0 = normal)

  // Targeting State
  target?: string | null; // ID of currently targeted entity

  // Invincibility State
  invincibilityTime?: number;

  // New Systems
  actionBar?: ActionBar;
  keyBindings?: KeyBindingsConfig;
  trainingProgress?: Record<string, number>; // Skill training progress

  // Debug / Cheat State
  isGodMode?: boolean;
  isNoclip?: boolean;
  speedMultiplier?: number;

  // Unified Animation State
  animation?: AnimationComponent;

  // [NEW] Rogue specific state (prototype)
  starBurstCount?: number;
  isReloading?: boolean;
  isInvisible?: boolean;
  isShielded?: boolean;

  // [NEW] World/Layer Context [Phase 5]
  worldContext?: WorldContext;
}

// UserCommand and InputPayload → now in ./types/Input.ts (re-exported above)

// Platform, SpawnRule, LevelEntity, PlatformerLevel
// → Canonical definitions live in ./LevelTypes.ts
// → Re-exported at the top of this file for backward compatibility.

// Game state interfaces
export interface GameState {
  player: Character;
  npcs: NPC[];
  targets: Character[];
  inCombat: boolean;
  sessionStartTime: number;
  xpGained: number;
  enemiesDefeated: number;
}

export interface NPC extends PhysicsEntity {
  name: string;
  type: 'training_dummy' | import('./npc/npcRoles').NpcRole;
  health: number;
  maxHealth: number;
  level: number;
  xpValue: number;
  respawns: boolean;
  lastRespawn: number;
  color: string;
  isHostile?: boolean;
  /** Talkable dialogue/shop/quest NPC (server `isNpc`). Friendly + inert —
   *  never a combat/projectile target (attackable dummies leave this unset). */
  isNpc?: boolean;
}

// Save data interfaces
export interface SaveData {
  version: string;
  character: Character;
  timestamp: number;
  slot: number;
}

export interface SaveSlot {
  slot: number;
  character?: Character;
  timestamp?: number;
  exists: boolean;
  created?: number;
}

// ── Re-exports from domain-specific type files ──────────────────────────────
// Extracted during 2026-03-17 tech debt audit. Barrel re-exports ensure
// all existing importers continue working unchanged.

export { JobClass, ProfessionType } from './types/Progression';
export type { JobAdvancement, TalentNode, TalentEffect, TalentTree, Profession, TrainingDummy } from './types/Progression';

export type { KeyBinding, KeyBindingsConfig, ActionBarSlot, ActionBar, UserCommand, InputPayload, PositionClaim } from './types/Input';

export type { CombatLog, TargetInfo, AnimationState, AnimationComponent, ChatMessage } from './types/Combat';
